import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addressesApi, AddressSuggestion } from '../services/api';
import { FormInput, FormInputProps } from './FormInput';

type AddressFields = { address: string; postalCode: string; city: string; country: string };

export function applySelectedAddress<T extends object>(previous: T, selected: AddressFields): T {
  const { address, postalCode, city, country } = selected;
  return { ...previous, address, postalCode, city, ...('country' in previous ? { country } : {}) };
}
type Props = Omit<FormInputProps, 'value' | 'onSelect' | 'children' | 'inputRef'> & {
  value: string;
  onAddressSelect: (address: AddressFields) => void;
};
type Status = 'idle' | 'loading' | 'empty' | 'unavailable' | 'selected' | 'incomplete';

export function AddressAutocomplete({
  value, onChange, onAddressSelect, onBlur, onKeyDown, onCompositionStart, onCompositionEnd,
  disabled, readOnly, id, ...props
}: Props) {
  const { t } = useTranslation();
  const uniqueId = useId();
  const listId = `${uniqueId}-addresses`;
  const statusId = `${uniqueId}-status`;
  const input = useRef<HTMLInputElement>(null);
  const session = useRef<Promise<string> | null>(null);
  const request = useRef<AbortController | null>(null);
  const revision = useRef(0);
  const composing = useRef(false);
  const latest = useRef({ value, disabled, readOnly });
  latest.current = { value, disabled, readOnly };
  const [query, setQuery] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [active, setActive] = useState(-1);
  const [status, setStatus] = useState<Status>('idle');

  const invalidate = () => {
    revision.current += 1;
    request.current?.abort();
    setSuggestions([]);
    setActive(-1);
  };

  const dismiss = () => {
    invalidate();
    setQuery(null);
    setStatus('idle');
    session.current = null;
  };

  useEffect(() => {
    const text = query?.trim() ?? '';
    if (query === null || query !== value || text.length < 3 || disabled || readOnly) return;
    const currentRevision = revision.current;
    const controller = new AbortController();
    request.current = controller;
    const timer = window.setTimeout(async () => {
      if (input.current?.matches(':disabled') || composing.current) return;
      setStatus('loading');
      try {
        if (!session.current) session.current = addressesApi.createSession();
        const token = await session.current;
        if (controller.signal.aborted || revision.current !== currentRevision) return;
        const results = await addressesApi.autocomplete(text, token, controller.signal);
        if (controller.signal.aborted || revision.current !== currentRevision) return;
        setSuggestions(results);
        setStatus(results.length ? 'idle' : 'empty');
      } catch {
        if (!controller.signal.aborted && revision.current === currentRevision) {
          session.current = null;
          setStatus('unavailable');
        }
      }
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, value, disabled, readOnly]);

  useEffect(() => () => {
    revision.current += 1;
    request.current?.abort();
    session.current = null;
  }, []);

  useEffect(() => {
    if (disabled || readOnly || (query !== null && query !== value)) {
      revision.current += 1;
      request.current?.abort();
      session.current = null;
      setQuery(null);
      setSuggestions([]);
      setActive(-1);
      setStatus('idle');
    }
  }, [disabled, readOnly, query, value]);

  useEffect(() => {
    if (active >= 0) document.getElementById(`${listId}-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, listId]);

  const select = async (suggestion: AddressSuggestion) => {
    if (disabled || readOnly || input.current?.matches(':disabled')) return;
    const tokenPromise = session.current;
    if (!tokenPromise) return;
    invalidate();
    setQuery(null);
    session.current = null; // Place Details terminates this session, including failed/aborted selections.
    const currentRevision = revision.current;
    const selectedInputValue = value;
    const controller = new AbortController();
    request.current = controller;
    setStatus('loading');
    try {
      const token = await tokenPromise;
      if (controller.signal.aborted || revision.current !== currentRevision) return;
      const address = await addressesApi.details(suggestion.placeId, token, controller.signal);
      if (controller.signal.aborted || revision.current !== currentRevision || latest.current.value !== selectedInputValue || latest.current.disabled || latest.current.readOnly) return;
      onAddressSelect({ address: address.address, postalCode: address.postalCode, city: address.city, country: address.country });
      setStatus(address.complete ? 'selected' : 'incomplete');
    } catch {
      if (!controller.signal.aborted && revision.current === currentRevision) setStatus('unavailable');
    }
  };

  const open = !disabled && !readOnly && query === value && suggestions.length > 0;
  return (
    <FormInput
      {...props}
      id={id || props.name || uniqueId}
      inputRef={input}
      value={value}
      maxLength={props.maxLength ?? 255}
      disabled={disabled}
      readOnly={readOnly}
      autoComplete="off"
      role="combobox"
      aria-autocomplete="list"
      aria-expanded={open}
      aria-controls={open ? listId : undefined}
      aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
      aria-describedby={[props['aria-describedby'], statusId].filter(Boolean).join(' ')}
      onChange={event => {
        invalidate();
        setStatus('idle');
        setQuery(composing.current ? null : event.target.value);
        if (!event.target.value.trim()) session.current = null;
        onChange?.(event);
      }}
      onBlur={event => { dismiss(); onBlur?.(event); }}
      onCompositionStart={event => { composing.current = true; dismiss(); onCompositionStart?.(event); }}
      onCompositionEnd={event => { composing.current = false; setQuery(event.currentTarget.value); onCompositionEnd?.(event); }}
      onKeyDown={event => {
        onKeyDown?.(event);
        if (event.defaultPrevented || event.nativeEvent.isComposing || composing.current) return;
        if (event.key === 'Escape') {
          if (open || status === 'loading') event.preventDefault();
          dismiss();
        } else if (open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
          event.preventDefault();
          setActive(index => event.key === 'ArrowDown' ? (index + 1) % suggestions.length : (index <= 0 ? suggestions.length - 1 : index - 1));
        } else if (open && event.key === 'Enter' && active >= 0) {
          event.preventDefault();
          void select(suggestions[active]);
        }
      }}
    >
      {/* The suggestions float over the form below the field, so nothing
          moves when they open or close. */}
      {open && (
        <div className="address-autocomplete-anchor">
        <div className="address-autocomplete-results border rounded shadow-sm">
          <ul id={listId} role="listbox" aria-label={t('addressAutocomplete.suggestions')} className="address-autocomplete-list list-unstyled mb-0">
            {suggestions.map((suggestion, index) => (
              <li key={suggestion.placeId} id={`${listId}-${index}`} role="option" aria-selected={index === active}
                className={`address-autocomplete-option px-3 py-2 ${index === active ? 'is-active' : ''}`}
                onMouseDown={event => event.preventDefault()}
                onClick={() => void select(suggestion)}>
                {suggestion.label}
              </li>
            ))}
          </ul>
          <div className="px-3 py-2 border-top">
            <img className="address-autocomplete-attribution" src="/assets/google-maps-attribution.svg" alt={t('addressAutocomplete.googleMaps')} />
          </div>
        </div>
        </div>
      )}
      {/* The visible text only changes for problems, so the form does not move
          while typing; progress and result counts are announced to screen readers. */}
      {!disabled && !readOnly && (
        <div id={statusId} className="form-text">
          {['empty', 'unavailable', 'incomplete'].includes(status) ? t(`addressAutocomplete.${status}`) : t('addressAutocomplete.help')}
        </div>
      )}
      <div role="status" aria-live="polite" className="visually-hidden">
        {!disabled && !readOnly && (status !== 'idle' ? t(`addressAutocomplete.${status}`) : open ? t('addressAutocomplete.resultCount', { count: suggestions.length }) : '')}
      </div>
    </FormInput>
  );
}
